import { useCallback, useEffect, useRef, useState } from "react";
import type { LockInfo, Person } from "../../lib/api/models";

export const HEARTBEAT_MS = 30_000;

export type { LockInfo };
export type LockHolder = Person;
export type AcquireResult = { status: "held" } | { status: "blocked"; lock: LockInfo };
export type LockApi = {
  acquire(docId: string, takeover: boolean): Promise<AcquireResult>;
  heartbeat(docId: string): Promise<"held" | "lost">;
  release(docId: string, keepalive: boolean): Promise<void>;
};
export type LockState =
  | { status: "idle" }
  | { status: "acquiring" }
  | { status: "held" }
  | { status: "blocked"; lock: LockInfo }
  | { status: "lost" }
  | { status: "error"; message: string };

const message = (e: unknown) => (e instanceof Error ? e.message : "The lock could not be taken.");

export function useSoftLock(docId: string, api: LockApi, heartbeatMs = HEARTBEAT_MS) {
  const [state, setState] = useState<LockState>({ status: "idle" });
  const held = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Bumped on every stop so a heartbeat still in flight cannot revive or lose a lock that was already left.
  const epoch = useRef(0);
  // Acquire requests still waiting for the server's answer.
  const acquiring = useRef(0);
  const apiRef = useRef(api);
  apiRef.current = api;

  const stop = useCallback(() => {
    epoch.current += 1;
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  }, []);

  const schedule = useCallback(() => {
    const mine = epoch.current;
    timer.current = setTimeout(() => {
      apiRef.current.heartbeat(docId).then(
        (h) => {
          if (mine !== epoch.current || !held.current) return;
          if (h === "lost") {
            held.current = false;
            stop();
            setState({ status: "lost" });
          } else {
            schedule();
          }
        },
        () => {
          // Offline: keep the lock and beat again at the steady 30 s rhythm (never longer than the 90 s stale window).
          if (mine !== epoch.current || !held.current) return;
          schedule();
        },
      );
    }, heartbeatMs);
  }, [docId, heartbeatMs, stop]);

  /**
   * `quiet`: a background retry while blocked keeps the current notice on screen and keeps it when the request fails.
   * It never starts while another acquire is waiting for its answer (it then returns idle without a request).
   */
  const acquire = useCallback(
    async (takeover = false, { quiet = false }: { quiet?: boolean } = {}): Promise<LockState> => {
      if (quiet && acquiring.current > 0) return { status: "idle" };
      if (!quiet) setState({ status: "acquiring" });
      stop();
      held.current = false;
      const mine = epoch.current;
      let next: LockState;
      try {
        let r: AcquireResult;
        acquiring.current += 1;
        try {
          r = await apiRef.current.acquire(docId, takeover);
        } finally {
          acquiring.current -= 1;
        }
        if (mine !== epoch.current) {
          // Left (release, pagehide, unmount) or superseded by a newer acquire while this request was in flight. The
          // grant is given back only when no newer acquire holds the lock or still waits for it: releasing would
          // drop the lock that one holds (a late background retry after Take over).
          if (r.status === "held" && !held.current && acquiring.current === 0)
            void apiRef.current.release(docId, true).catch(() => {});
          return { status: "idle" };
        }
        if (r.status === "held") {
          held.current = true;
          schedule();
          next = { status: "held" };
        } else {
          next = { status: "blocked", lock: r.lock };
        }
      } catch (e) {
        if (mine !== epoch.current) return { status: "idle" };
        next = { status: "error", message: message(e) };
        if (quiet) return next;
      }
      setState(next);
      return next;
    },
    [docId, schedule, stop],
  );

  const release = useCallback(async () => {
    stop();
    setState({ status: "idle" });
    if (!held.current) return;
    held.current = false;
    await apiRef.current.release(docId, false).catch(() => {});
  }, [docId, stop]);

  useEffect(() => {
    const onPageHide = () => {
      stop();
      if (!held.current) return;
      held.current = false;
      void apiRef.current.release(docId, true).catch(() => {});
      setState({ status: "lost" });
    };
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      stop();
      if (held.current) {
        held.current = false;
        void apiRef.current.release(docId, true).catch(() => {});
      }
    };
  }, [docId, stop]);

  return { state, acquire, release };
}
